/**
 * DocumentControlBar - CRUD and revision controls for documents
 * 
 * Provides AS9100D compliant document lifecycle controls:
 * - Status changes (Draft → In Review → Approved → Released)
 * - Create new revision (from Released documents)
 * - Delete draft revision
 * - Submit for approval
 */

import { useState } from 'react';
import { projectsApi, companyDocumentsApi } from '../../services/api';
import { 
  useToast, 
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Label,
  Textarea,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import type { DocumentRevision } from '../../types';

interface DocumentControlBarProps {
  documentNumber: string;
  projectId?: string;  // Only for project documents
  documentType: 'project' | 'company';
  currentStatus: string;
  currentRevision: string;
  revisions: DocumentRevision[];
  onRefresh: () => void;
  onEnableEdit?: () => void;
  canEdit: boolean;
}

function getNextRevision(current: string): string {
  if (current === 'Draft') return 'A';
  if (current.length === 1 && current < 'Z') {
    return String.fromCharCode(current.charCodeAt(0) + 1);
  }
  return current + '.1';
}

export default function DocumentControlBar({
  documentNumber,
  projectId,
  documentType,
  currentStatus,
  currentRevision,
  revisions,
  onRefresh,
  onEnableEdit,
  canEdit,
}: DocumentControlBarProps) {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComments, setStatusComments] = useState('');
  const [revisionDescription, setRevisionDescription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { success, error } = useToast();

  const isReleased = currentStatus === 'Released';
  const isApproved = currentStatus === 'Approved';
  const isDraft = currentStatus === 'Draft';
  const isInReview = currentStatus === 'In Review';
  const canCreateRevision = isReleased;
  const canDelete = isDraft && revisions.length > 1;
  const canSubmitForApproval = isDraft;

  const handleChangeStatus = async () => {
    if (!newStatus) {
      error('Validation Error', 'Please select a status');
      return;
    }

    setIsProcessing(true);
    try {
      if (documentType === 'project' && projectId) {
        await projectsApi.changeStatus(
          projectId,
          documentNumber,
          newStatus,
          'User', // TODO: Get from auth context
          statusComments
        );
      } else {
        await companyDocumentsApi.updateStatus(
          documentNumber,
          newStatus,
          'User', // TODO: Get from auth context
          statusComments
        );
      }

      setShowStatusModal(false);
      setNewStatus('');
      setStatusComments('');
      onRefresh();
    } catch (err: unknown) {
      error('Status Change Failed', err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateRevision = async () => {
    if (!revisionDescription || revisionDescription.length < 10) {
      error('Validation Error', 'Please provide a detailed change description (minimum 10 characters)');
      return;
    }

    setIsProcessing(true);
    try {
      if (documentType === 'project' && projectId) {
        await projectsApi.createRevision(
          projectId,
          documentNumber,
          revisionDescription,
          '', // generatedPath - backend generates this
          'User' // TODO: Get from auth context
        );
      } else {
        await companyDocumentsApi.createRevision(
          documentNumber,
          revisionDescription,
          'User' // TODO: Get from auth context
        );
      }

      success('Revision Created', `Revision ${getNextRevision(currentRevision)} created successfully!`);
      setShowRevisionModal(false);
      setRevisionDescription('');
      onRefresh();
    } catch (err: unknown) {
      error('Failed to Create Revision', err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteDraft = async () => {
    setIsProcessing(true);
    try {
      if (documentType === 'project' && projectId) {
        await projectsApi.deleteRevision(
          projectId,
          documentNumber,
          currentRevision,
          'User' // TODO: Get from auth context
        );
      } else {
        await companyDocumentsApi.deleteRevision(documentNumber, currentRevision);
      }

      success('Draft Deleted', 'Draft revision deleted successfully!');
      setShowDeleteModal(false);
      onRefresh();
    } catch (err: unknown) {
      error('Failed to Delete Revision', err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitForApproval = async () => {
    setIsProcessing(true);
    try {
      if (documentType === 'project' && projectId) {
        await projectsApi.changeStatus(
          projectId,
          documentNumber,
          'In Review',
          'User',
          'Submitted for approval'
        );
      } else {
        await companyDocumentsApi.updateStatus(
          documentNumber,
          'In Review',
          'User',
          'Submitted for approval'
        );
      }

      setShowSubmitModal(false);
      onRefresh();
    } catch (err: unknown) {
      error('Submission Failed', err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Control Bar */}
      <div className="flex items-center gap-2 flex-shrink-0 bg-muted border-b border-border px-4 py-2">
        {/* Status Badge */}
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
          isReleased ? 'bg-success-light text-success' :
          isApproved ? 'bg-info-light text-info' :
          isInReview ? 'bg-warning-light text-warning' :
          'bg-muted text-muted-foreground'
        }`}>
          {currentStatus} (Rev {currentRevision})
        </span>

        <div className="flex-1" />

        {/* Edit Button */}
        {canEdit && onEnableEdit && (
          <Button
            onClick={onEnableEdit}
            data-edit-button="true"
            size="xs"
          >
            ✏️ Edit
          </Button>
        )}

        {/* Submit for Approval */}
        {canSubmitForApproval && (
          <Button
            onClick={() => setShowSubmitModal(true)}
            disabled={isProcessing}
            variant="success"
            size="xs"
          >
            ✓ Submit for Approval
          </Button>
        )}

        {/* Create New Revision */}
        {canCreateRevision && (
          <Button
            onClick={() => setShowRevisionModal(true)}
            disabled={isProcessing}
            variant="secondary"
            size="xs"
            title="Create new draft revision from this released document"
          >
            🔄 New Revision ({getNextRevision(currentRevision)})
          </Button>
        )}

        {/* Change Status */}
        <Button
          onClick={() => setShowStatusModal(true)}
          disabled={isProcessing}
          variant="secondary"
          size="xs"
        >
          📊 Status
        </Button>

        {/* Delete Draft */}
        {canDelete && (
          <Button
            onClick={() => setShowDeleteModal(true)}
            disabled={isProcessing}
            variant="destructive"
            size="xs"
            title="Delete current draft revision"
          >
            🗑️ Delete Draft
          </Button>
        )}
      </div>

      {/* Change Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={(open) => {
        if (!open) {
          setShowStatusModal(false);
          setNewStatus('');
          setStatusComments('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Document Status</DialogTitle>
            <DialogDescription>
              Update the document lifecycle status for AS9100D compliance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-status">New Status</Label>
              <RadixSelect
                value={newStatus} 
                onValueChange={setNewStatus}
              >
                <SelectTrigger id="new-status">
                  <SelectValue placeholder="-- Select Status --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="In Review">In Review</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Released">Released</SelectItem>
                </SelectContent>
              </RadixSelect>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status-comments">Comments (optional)</Label>
              <Textarea
                id="status-comments"
                value={statusComments}
                onChange={(e) => setStatusComments(e.target.value)}
                rows={3}
                placeholder="Reason for status change..."
              />
            </div>

            <div className="bg-info-light rounded-md p-3">
              <p className="text-sm text-info">
                Current: <strong>{currentStatus}</strong> → New: <strong>{newStatus || '(none)'}</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowStatusModal(false);
                setNewStatus('');
                setStatusComments('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleChangeStatus}
              disabled={!newStatus || isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Change Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Revision Modal */}
      <Dialog open={showRevisionModal} onOpenChange={(open) => {
        if (!open) {
          setShowRevisionModal(false);
          setRevisionDescription('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Revision</DialogTitle>
            <DialogDescription>
              Create a new draft revision from the current released document.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-secondary rounded-md p-3">
              <p className="text-sm text-muted-foreground">
                Creating: <strong>Revision {getNextRevision(currentRevision)}</strong>
                <br />
                <span className="text-xs">Current revision will be marked as "Superseded"</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="revision-description">Change Description *</Label>
              <Textarea
                id="revision-description"
                value={revisionDescription}
                onChange={(e) => setRevisionDescription(e.target.value)}
                rows={4}
                placeholder="Describe what changed in this revision (minimum 10 characters)&#10;Examples:&#10;- Updated test approach per DER feedback&#10;- Added ARINC429 interface requirements&#10;- Corrected software level allocation"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                {revisionDescription.length}/10 characters minimum
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowRevisionModal(false);
                setRevisionDescription('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRevision}
              disabled={revisionDescription.length < 10 || isProcessing}
            >
              {isProcessing ? 'Creating...' : `Create Revision ${getNextRevision(currentRevision)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Draft Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Draft Revision?</DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-destructive-light rounded-md p-3 border-l-4 border-destructive">
              <p className="text-sm text-destructive">
                <strong>Warning:</strong> This will permanently delete the current draft revision ({currentRevision}).
                <br />
                <span className="text-xs">The document will revert to the previous released revision.</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setShowDeleteModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteDraft}
              disabled={isProcessing}
              variant="destructive"
            >
              {isProcessing ? 'Deleting...' : 'Delete Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit for Approval Modal */}
      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit for Approval?</DialogTitle>
            <DialogDescription>
              Submit this document for review by designated approvers.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-success-light rounded-md p-3 border-l-4 border-success">
              <p className="text-sm text-success">
                This will change the document status from <strong>Draft</strong> to <strong>In Review</strong>.
                <br />
                <span className="text-xs">Reviewers will be notified and can approve or request changes.</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setShowSubmitModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitForApproval}
              disabled={isProcessing}
              variant="success"
            >
              {isProcessing ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
